'use client';
import React from 'react';
import Image from 'next/image';
import AnnouncementBar from '@/components/AnnouncementBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function TreatmentStagesPage() {
  return (
    <div style={{ backgroundColor: '#EFE3E1', minHeight: '100vh' }}>
      <style>{`
        .cta-btn-dark {
          background-color: #1A2332;
          transition: background-color 0.25s ease;
        }
        .cta-btn-dark:hover {
          background-color: #2C2C2C;
        }
      `}</style>
      <AnnouncementBar />
      <Header />
      <main>
        {/* ── Page Hero ── */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: '#1A2332',
            paddingTop: 'clamp(72px, 9vw, 128px)',
            paddingBottom: 'clamp(72px, 9vw, 128px)',
          }}
        >
          {/* Eagerly-loaded background image — served as WebP via Next.js Image optimisation */}
          <Image
            src="/assets/images/image_2026-06-21_183958361-1782063601290.png"
            alt=""
            fill
            sizes="100vw"
            quality={80}
            priority
            className="object-cover"
            style={{ objectPosition: 'center' }}
          />
          {/* Dark overlay */}
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(26,35,50,0.65)' }}
          />
          <div className="relative z-10" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: '11px',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#8A9E8C',
                marginBottom: '20px',
              }}
            >
              The Process
            </p>
            <h1
              style={{
                fontFamily: 'orpheus-pro, Georgia, serif',
                fontSize: 'clamp(36px, 5vw, 72px)',
                fontWeight: '400',
                color: '#F5F0EA',
                lineHeight: '1.1',
                maxWidth: '640px',
                marginBottom: '24px',
              }}
            >
              Treatment<br />
              <em style={{ fontStyle: 'normal' }}>Stages</em>
            </h1>
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: 'clamp(16px, 1.4vw, 20px)',
                color: 'rgba(239,227,225,0.65)',
                lineHeight: '1.7',
                maxWidth: '520px',
              }}
            >
              Understanding the journey from first contact to lasting change.
            </p>
          </div>
        </section>

        {/* ── Stage 01: Psychological Assessment ── */}
        <section
          className="w-full"
          style={{ backgroundColor: '#EFE3E1', paddingTop: 'clamp(64px, 8vw, 112px)', paddingBottom: 'clamp(64px, 8vw, 112px)' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20">

              {/* Stage marker */}
              <div style={{ flex: '0 0 auto', maxWidth: '280px' }}>
                <span
                  style={{
                    display: 'block',
                    fontFamily: 'orpheus-pro, Georgia, serif',
                    fontSize: 'clamp(56px, 8vw, 120px)',
                    fontWeight: '400',
                    color: 'rgba(26,35,50,0.07)',
                    lineHeight: '1',
                    marginBottom: '16px',
                  }}
                >
                  01
                </span>
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '11px',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#8A9E8C',
                    marginBottom: '8px',
                  }}
                >
                  Stage One
                </p>
                <h2
                  style={{
                    fontFamily: 'orpheus-pro, Georgia, serif',
                    fontSize: 'clamp(22px, 2.5vw, 36px)',
                    fontWeight: '400',
                    color: '#1A2332',
                    lineHeight: '1.25',
                  }}
                >
                  Psychological Assessment
                </h2>
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {[
                    'It can be difficult to discuss personal issues and Dr Morgan aims to create a safe, confidential and supportive space for you to explore the possible benefits of engaging in psychological therapy. Clinical psychologists have extensive training in assessing a range of psychological difficulties and delivering the most appropriate form of help.',
                    'Your initial appointment will last 1 hr and is an opportunity to gather information from you about how you see your current difficulties and how you would like therapy to move forward.',
                    'Dr Morgan will never work outside of the remit of her professional competencies and training. Therefore if she feels that she\'s not the right therapist to help you move forward then she will happily signpost you to alternative services and will make recommendations via your GP if you so wish.',
                    'Dr Morgan can offer an assessment only service. This can be requested by individuals looking for a "one off" psychology report. Dr Morgan will make sure that any assessment is linked with future recommendations for your psychological well-being and although you\'re under no obligation to seek therapy, she will signpost you to your GP and NHS if you do not wish to be treated privately.',
                  ]?.map((para, idx) => (
                    <p
                      key={idx}
                      style={{
                        fontFamily: 'adobe-garamond-pro, Georgia, serif',
                        fontSize: 'clamp(16px, 1.3vw, 19px)',
                        color: '#4A4A4A',
                        lineHeight: '1.8',
                      }}
                    >
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Quote banner ── */}
        <section
          className="relative w-full overflow-hidden"
          style={{ backgroundColor: '#1A2332', paddingTop: 'clamp(48px, 6vw, 80px)', paddingBottom: 'clamp(48px, 6vw, 80px)' }}
        >
          <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)', textAlign: 'center' }}>
            <p
              style={{
                fontFamily: 'orpheus-pro, Georgia, serif',
                fontSize: 'clamp(20px, 2.5vw, 36px)',
                fontWeight: '400',
                fontStyle: 'normal',
                color: '#F5F0EA',
                lineHeight: '1.5',
                marginBottom: '16px',
              }}
            >
              People underestimate their capacity for change.
            </p>
            <cite
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: '13px',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'rgba(239,227,225,0.4)',
                fontStyle: 'normal',
              }}
            >
              — John Porter
            </cite>
          </div>
        </section>

        {/* ── Stage 02: Therapy ── */}
        <section
          className="w-full"
          style={{ backgroundColor: '#E8D8D5', paddingTop: 'clamp(64px, 8vw, 112px)', paddingBottom: 'clamp(64px, 8vw, 112px)' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <div className="flex flex-col lg:flex-row-reverse items-start gap-12 lg:gap-20">

              {/* Stage marker */}
              <div style={{ flex: '0 0 auto', maxWidth: '280px' }}>
                <span
                  style={{
                    display: 'block',
                    fontFamily: 'orpheus-pro, Georgia, serif',
                    fontSize: 'clamp(56px, 8vw, 120px)',
                    fontWeight: '400',
                    color: 'rgba(26,35,50,0.07)',
                    lineHeight: '1',
                    marginBottom: '16px',
                  }}
                >
                  02
                </span>
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '11px',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#8A9E8C',
                    marginBottom: '8px',
                  }}
                >
                  Stage Two
                </p>
                <h2
                  style={{
                    fontFamily: 'orpheus-pro, Georgia, serif',
                    fontSize: 'clamp(22px, 2.5vw, 36px)',
                    fontWeight: '400',
                    color: '#1A2332',
                    lineHeight: '1.25',
                  }}
                >
                  Therapy
                </h2>
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '32px' }}>
                  <p
                    style={{
                      fontFamily: 'adobe-garamond-pro, Georgia, serif',
                      fontSize: 'clamp(16px, 1.3vw, 19px)',
                      color: '#4A4A4A',
                      lineHeight: '1.8',
                    }}
                  >
                    In addition to extensive training in assessing a range of psychological difficulties, following the formulation of an individual&apos;s distress, Clinical Psychologists are also trained in providing more than one type of therapy. Dr Morgan has experience in delivering therapy using a number of models including Cognitive Behavioural Therapy (CBT), Cognitive Analytic therapy (CAT), Mindfulness based Cognitive Therapy (MCT), Systemic Therapy (ST) and Psychodynamic Psycho Therapy (PPT).
                  </p>
                  <p
                    style={{
                      fontFamily: 'adobe-garamond-pro, Georgia, serif',
                      fontSize: 'clamp(16px, 1.3vw, 19px)',
                      color: '#4A4A4A',
                      lineHeight: '1.8',
                    }}
                  >
                    CBT is the main mode of therapy that Dr Morgan offers. Any intervention offered is based upon individual need and the formulation of current difficulties.
                  </p>
                </div>

                {/* What will happen in therapy */}
                <h3
                  style={{
                    fontFamily: 'orpheus-pro, Georgia, serif',
                    fontSize: 'clamp(18px, 2vw, 28px)',
                    fontWeight: '400',
                    fontStyle: 'normal',
                    color: '#1A2332',
                    lineHeight: '1.3',
                    marginBottom: '20px',
                  }}
                >
                  What will happen in therapy?
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {[
                    'The first thing that you should expect is to meet with someone who will listen to you. For Dr Morgan, listening means hearing you express your issues, how you and others perceive them, how long they have been occurring and how you have coped.',
                    'Some of your sessions may be devoted more toward listening; other sessions might be spent more actively engaged in trying to solve a particular problem. This may involve keeping a diary of thoughts, feelings and behaviours, or conducting a task to test out the validity of a particular belief.',
                    'Since therapy involves the exploration and processing of uncomfortable aspects of life, you may at times experience difficult feelings like sadness, guilt, anger, depression, frustration, loneliness, and helplessness. These emotional states can sometimes last for days or even weeks. It is important that you are aware of this, however research has shown that most people who take these risks find that therapy is helpful.',
                    'Last, but by no means least, you should expect to be attended to and treated with respect and dignity with confidentiality assured.',
                  ]?.map((para, idx) => (
                    <p
                      key={idx}
                      style={{
                        fontFamily: 'adobe-garamond-pro, Georgia, serif',
                        fontSize: 'clamp(16px, 1.3vw, 19px)',
                        color: '#4A4A4A',
                        lineHeight: '1.8',
                      }}
                    >
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Quote banner 2 ── */}
        <section
          className="w-full"
          style={{ backgroundColor: '#E8D8D5', paddingTop: 'clamp(64px, 8vw, 112px)', paddingBottom: 'clamp(64px, 8vw, 112px)' }}
        >
          <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)', textAlign: 'center' }}>
            <p
              style={{
                fontFamily: 'orpheus-pro, Georgia, serif',
                fontSize: 'clamp(20px, 2.5vw, 36px)',
                fontWeight: '400',
                fontStyle: 'normal',
                color: '#F5F0EA',
                lineHeight: '1.5',
                marginBottom: '16px',
              }}
            >
              &ldquo;The little things are infinitely the most important.&rdquo;
            </p>
            <cite
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: '13px',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'rgba(239,227,225,0.4)',
                fontStyle: 'normal',
              }}
            >
              — Sherlock Holmes by Arthur Conan Doyle
            </cite>
          </div>
        </section>

        {/* ── Confidentiality assurance ── */}
        <section
          className="w-full"
          style={{ backgroundColor: '#DFD0CC', paddingTop: 'clamp(64px, 8vw, 96px)', paddingBottom: 'clamp(64px, 8vw, 96px)' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div style={{ maxWidth: '600px' }}>
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '11px',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#8A9E8C',
                    marginBottom: '12px',
                  }}
                >
                  Confidentiality
                </p>
                <h2
                  style={{
                    fontFamily: 'orpheus-pro, Georgia, serif',
                    fontSize: 'clamp(24px, 3vw, 44px)',
                    fontWeight: '400',
                    color: '#1A2332',
                    lineHeight: '1.2',
                    marginBottom: '16px',
                  }}
                >
                  Assurance
                </h2>
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: 'clamp(15px, 1.2vw, 18px)',
                    color: '#4A4A4A',
                    lineHeight: '1.75',
                  }}
                >
                  All services are bound by professional codes of conduct, so a confidential service is assured. If you require particular discretion around consultations, then please don&apos;t hesitate to contact Dr Morgan to discuss in further detail so that confidentiality is maintained.
                </p>
              </div>
              <a
                href="/contact"
                className="cta-btn-dark"
                style={{
                  fontFamily: 'adobe-garamond-pro, Georgia, serif',
                  fontSize: '13px',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#EFE3E1',
                  padding: '15px 40px',
                  textDecoration: 'none',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              >
                Arrange a Consultation
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
